/**
 * Skattekalender 2025 - Exakta förfallodatum för svenska företag
 * Baserat på officiell data från Skatteverket
 */

export interface TaxDueDate {
  month: number;
  preliminaryTaxSmall: number;  // Dag i månaden
  employerFeesSmall: number;    // Dag i månaden  
  vatSmall: number | 'quarterly'; // Dag i månaden eller 'quarterly'
  preliminaryTaxLarge: number;  // Dag i månaden
  employerFeesLarge: number;    // Dag i månaden
  vatLarge: number;            // Dag i månaden (alltid månadsvis för stora bolag)
}

export const TAX_CALENDAR_2025: TaxDueDate[] = [
  { month: 1, preliminaryTaxSmall: 17, employerFeesSmall: 17, vatSmall: 17, preliminaryTaxLarge: 17, employerFeesLarge: 17, vatLarge: 26 },
  { month: 2, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 'quarterly', preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 }, // Q4
  { month: 3, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 12, preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 },
  { month: 4, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 12, preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 },
  { month: 5, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 'quarterly', preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 }, // Q1
  { month: 6, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 12, preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 },
  { month: 7, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 12, preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 },
  { month: 8, preliminaryTaxSmall: 17, employerFeesSmall: 17, vatSmall: 17, preliminaryTaxLarge: 17, employerFeesLarge: 17, vatLarge: 26 }, // Q2 - UPPDATERAT: 17:e även för kvartalsvis moms
  { month: 9, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 12, preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 },
  { month: 10, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 12, preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 },
  { month: 11, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 'quarterly', preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 }, // Q3
  { month: 12, preliminaryTaxSmall: 12, employerFeesSmall: 12, vatSmall: 12, preliminaryTaxLarge: 12, employerFeesLarge: 12, vatLarge: 26 }
];

export interface CompanyConfig {
  companySize: 'small' | 'large'; // <40M kr vs >40M kr
  vatReportingPeriod: 'monthly' | 'quarterly';
}

/**
 * Beräknar exakt förfallodatum för given period och företagskonfiguration
 */
export function getExactDueDate(
  period: string, 
  taxType: 'preliminary_tax' | 'employer_fees' | 'vat',
  config: CompanyConfig
): { dueDate: string; dueDateDay: number } | null {
  // Parse period (format: YYYYMM)
  const year = parseInt(period.slice(0, 4));
  const month = parseInt(period.slice(4, 6));
  
  if (year !== 2025 || month < 1 || month > 12) {
    return null; // Endast 2025 stöds för nu
  }
  
  const calendarEntry = TAX_CALENDAR_2025[month - 1];
  let dueDateDay: number;
  
  // Beräkna förfallodatum baserat på skattetyp och företagsstorlek
  switch (taxType) {
    case 'preliminary_tax':
      dueDateDay = config.companySize === 'large' 
        ? calendarEntry.preliminaryTaxLarge 
        : calendarEntry.preliminaryTaxSmall;
      break;
      
    case 'employer_fees':
      dueDateDay = config.companySize === 'large'
        ? calendarEntry.employerFeesLarge
        : calendarEntry.employerFeesSmall;
      break;
      
    case 'vat':
      if (config.companySize === 'large') {
        dueDateDay = calendarEntry.vatLarge;
      } else {
        // Små bolag
        if (config.vatReportingPeriod === 'monthly') {
          dueDateDay = typeof calendarEntry.vatSmall === 'number' ? calendarEntry.vatSmall : 12;
        } else {
          // Kvartalsvis - kontrollera om denna månad är en kvartalsrapport
          if (calendarEntry.vatSmall === 'quarterly') {
            // Speciell hantering för augusti (Q2) - 17:e istället för 12:e
            dueDateDay = month === 8 ? 17 : 12;
          } else if (typeof calendarEntry.vatSmall === 'number' && calendarEntry.vatSmall === 17) {
            // Augusti för månadsvis moms - använd 17:e även för kvartalsvis
            dueDateDay = 17;
          } else {
            return null; // Ingen momsrapport denna månad för kvartalsvis
          }
        }
      }
      break;
      
    default:
      return null;
  }
  
  // Skapa datum för förfallodagen (nästa månad för de flesta)
  let dueMonth = month + 1;
  let dueYear = year;
  
  if (dueMonth > 12) {
    dueMonth = 1;
    dueYear++;
  }
  
  const dueDate = `${dueYear}-${dueMonth.toString().padStart(2, '0')}-${dueDateDay.toString().padStart(2, '0')}`;
  
  return { dueDate, dueDateDay };
}

/**
 * Kontrollerar om en betalning är sen baserat på exakt kalender
 */
export function isPaymentLate(
  period: string,
  paymentDate: string,
  taxType: 'preliminary_tax' | 'employer_fees' | 'vat',
  config: CompanyConfig,
  toleranceDays: number = 3
): { isLate: boolean; daysLate: number; actualDueDate: string } {
  const dueDateInfo = getExactDueDate(period, taxType, config);
  
  if (!dueDateInfo) {
    // Fallback till generell regel om inget hittas
    const year = parseInt(period.slice(0, 4));
    const month = parseInt(period.slice(4, 6));
    let dueMonth = month + 1;
    let dueYear = year;
    
    if (dueMonth > 12) {
      dueMonth = 1;
      dueYear++;
    }
    
    const fallbackDueDate = `${dueYear}-${dueMonth.toString().padStart(2, '0')}-12`;
    const paymentTimestamp = new Date(paymentDate).getTime();
    const dueTimestamp = new Date(fallbackDueDate).getTime();
    const toleranceTimestamp = dueTimestamp + (toleranceDays * 24 * 60 * 60 * 1000);
    
    const daysLate = Math.max(0, Math.ceil((paymentTimestamp - toleranceTimestamp) / (24 * 60 * 60 * 1000)));
    
    return {
      isLate: paymentTimestamp > toleranceTimestamp,
      daysLate,
      actualDueDate: fallbackDueDate
    };
  }
  
  const { dueDate } = dueDateInfo;
  const paymentTimestamp = new Date(paymentDate).getTime();
  const dueTimestamp = new Date(dueDate).getTime();
  const toleranceTimestamp = dueTimestamp + (toleranceDays * 24 * 60 * 60 * 1000);
  
  const daysLate = Math.max(0, Math.ceil((paymentTimestamp - toleranceTimestamp) / (24 * 60 * 60 * 1000)));
  
  return {
    isLate: paymentTimestamp > toleranceTimestamp,
    daysLate,
    actualDueDate: dueDate
  };
}

/**
 * Genererar användarbar beskrivning av förfallodatum för given konfiguration
 */
export function getCompanyTaxScheduleDescription(config: CompanyConfig): string {
  const sizeText = config.companySize === 'large' ? 'Stort bolag (>40M kr)' : 'Litet bolag (<40M kr)';
  const vatText = config.vatReportingPeriod === 'monthly' ? 'månadsvis' : 'kvartalsvis';
  
  if (config.companySize === 'large') {
    return `${sizeText}: Preliminärskatt och arbetsgivaravgifter den 12:e (17:e jan/aug), moms månadsvis den 26:e`;
  } else {
    return `${sizeText}: Preliminärskatt och arbetsgivaravgifter den 12:e (17:e jan/aug), moms ${vatText} ${config.vatReportingPeriod === 'monthly' ? 'den 12:e' : 'feb/maj/aug/nov'}`;
  }
}

/**
 * Listar alla förfallodatum för året för given konfiguration
 */
export function getYearlyTaxSchedule(config: CompanyConfig): Array<{
  month: string;
  preliminaryTax: string;
  employerFees: string;
  vat: string;
}> {
  return TAX_CALENDAR_2025.map(entry => {
    const monthName = new Date(2025, entry.month - 1, 1).toLocaleString('sv-SE', { month: 'long' });
    
    let vatInfo = '';
    if (config.companySize === 'large') {
      vatInfo = `${entry.vatLarge}:e`;
    } else {
      if (config.vatReportingPeriod === 'monthly') {
        vatInfo = typeof entry.vatSmall === 'number' ? `${entry.vatSmall}:e` : '12:e';
      } else {
        // Kvartalsvis moms för små bolag
        if (entry.vatSmall === 'quarterly') {
          vatInfo = entry.month === 8 ? '17:e (Q2)' : '12:e (kvartalsvis)';
        } else if (typeof entry.vatSmall === 'number' && entry.vatSmall === 17) {
          vatInfo = '17:e (kvartalsvis)';
        } else {
          vatInfo = '-';
        }
      }
    }
    
    return {
      month: monthName,
      preliminaryTax: `${config.companySize === 'large' ? entry.preliminaryTaxLarge : entry.preliminaryTaxSmall}:e`,
      employerFees: `${config.companySize === 'large' ? entry.employerFeesLarge : entry.employerFeesSmall}:e`,
      vat: vatInfo
    };
  });
}

/**
 * Genererar skattekalender-text för systemprompt baserat på företagskonfiguration
 */
export function generateTaxCalendarPrompt(config: CompanyConfig, year?: number): string {
  const displayYear = year || new Date().getFullYear();
  
  if (config.companySize === 'large') {
    return `SVENSK SKATTEKALENDER ${displayYear} (STORT FÖRETAG >40M kr):
- Preliminärskatt & Arbetsgivaravgifter: Den 12:e i månaden efter perioden (17:e för jan & aug)
- Moms (alltid månatlig): Den 26:e i månaden efter perioden

Grace period: 3 dagar efter förfallodatum.`;
  } else {
    const vatText = config.vatReportingPeriod === 'monthly' 
      ? '- Moms (månatlig): Den 12:e i månaden efter perioden (17:e för jan & aug)'
      : '- Moms (kvartalsvis): 12 feb (Q4), 12 maj (Q1), 17 aug (Q2), 12 nov (Q3)';
    
    return `SVENSK SKATTEKALENDER ${displayYear} (LITET FÖRETAG <40M kr):
- Preliminärskatt & Arbetsgivaravgifter: Den 12:e i månaden efter perioden (17:e för jan & aug)
${vatText}

Grace period: 3 dagar efter förfallodatum.`;
  }
}
